/**
 * @module builder
 */
import type { EditableRate, ProductOption } from '../index'
import { parseNumber } from '../../../calculator/util/parse-number'

/**
 * A product carries three coverage rates with three *different* units, and a rate carries a single
 * unitless `coverage` override that consumers interpret using the rate's `rateType`. This is the family
 * that interpretation resolves to:
 *
 * - `quantity` → gal/item, and it **multiplies** (`volume = coverage * qty * coats`)
 * - `linear`   → lnft/gal, and it **divides**   (`volume = lnft / coverage * qty * coats`)
 * - `square`   → sqft/gal, and it **divides**
 *
 * Because the families divide vs multiply, a value that is sane in one family is off by orders of
 * magnitude in another — a 400 sqft/gal coverage read as 400 gal/item produced an $86k material line on
 * a single closet (#22383).
 *
 * Mirrors the branch in `calculator/area-substrates/product-volume.ts`.
 */
export type CoverageUnitFamily = 'quantity' | 'linear' | 'square'

export function getCoverageUnitFamily(rateType: string): CoverageUnitFamily {
  // 'none' is the rateType for flat $ and flat Hours rates (Variable 2 === 'none' in RateTypeSelect).
  // overridePriceSubstrate/overrideHoursSubstrate always compute volume with the quantity formula for
  // these rates, regardless of the rate's prior Variables, so their coverage family must match (ENG-1240).
  if (rateType === 'quantity' || rateType === 'none') {
    return 'quantity'
  }
  if (rateType === 'lnft') {
    return 'linear'
  }
  return 'square'
}

/**
 * A rate's coverage override is measured in the unit its `rateType` implies. When that unit changes, the
 * stored number no longer means what the estimator typed, so the rate dialog clears it (ENG-1240).
 * Switching between surfaces inside one family (sqftWalls → sqftCeiling) keeps the same unit and the same
 * math, so the value survives.
 */
export function shouldClearRateCoverageOnRateTypeChange({
  previousRateType,
  nextRateType
}: {
  previousRateType: string
  nextRateType: string
}): boolean {
  return getCoverageUnitFamily(previousRateType) !== getCoverageUnitFamily(nextRateType)
}

/**
 * The product's own coverage rate for the unit family a given rateType implies — i.e. the value that
 * applies when a rate carries no coverage override of its own.
 */
export function getProductCoverageForRateType({
  product,
  rateType
}: {
  product: Pick<ProductOption, 'coverage_rate' | 'lnft_coverage' | 'quantity_coverage'>
  rateType: string
}): number {
  if (!product) {
    return 0
  }

  // `parseNumber(null)` returns null rather than its fallback, so coerce.
  switch (getCoverageUnitFamily(rateType)) {
    case 'quantity':
      return parseNumber(product.quantity_coverage, 0) || 0
    case 'linear':
      return parseNumber(product.lnft_coverage, 0) || 0
    default:
      return parseNumber(product.coverage_rate, 0) || 0
  }
}

/**
 * An empty rate coverage override means "use the product's own coverage" — every consumer skips it.
 * `'null'` shows up because the value is round-tripped through `String()` in the rates dialog.
 */
export function isEmptyRateCoverage(coverage: string | number | null | undefined): boolean {
  if (coverage === null || typeof coverage === 'undefined' || coverage === '' || coverage === 'null') {
    return true
  }
  return parseNumber(coverage, 0) === 0
}

/**
 * Thresholds chosen to be deliberately conservative: they should fire on a value that is plainly from the
 * wrong unit family and stay quiet on anything a real estimator might mean. Real gal/item coverages sit
 * around 0.05–2; real sqft-or-lnft-per-gallon coverages sit in the hundreds.
 */
const MAX_PLAUSIBLE_QUANTITY_COVERAGE = 10
const MIN_PLAUSIBLE_PER_GALLON_COVERAGE = 1

/**
 * True when a rate's coverage override looks like it was entered for a different unit family than the
 * rate's current `rateType` — e.g. `400` on a per-item rate, or `0.05` on a per-lnft rate.
 *
 * Advisory only. We never rewrite or discard the value: switching a rate's type keeps whatever the
 * estimator typed, and this just makes a suspicious number visible (#22383).
 */
export function isImplausibleRateCoverage({
  coverage,
  rateType
}: {
  coverage: string | number | null | undefined
  rateType: string
}): boolean {
  if (isEmptyRateCoverage(coverage)) {
    return false
  }

  // isEmptyRateCoverage has already rejected null and anything that parses to 0.
  const value = parseNumber(coverage, 0)
  if (!Number.isFinite(value) || value < 0) {
    return false
  }

  if (getCoverageUnitFamily(rateType) === 'quantity') {
    return value > MAX_PLAUSIBLE_QUANTITY_COVERAGE
  }

  return value < MIN_PLAUSIBLE_PER_GALLON_COVERAGE
}

/**
 * The coverage a rate has configured for a specific default product, e.g. so re-selecting a
 * product that was removed from a surface restores its coverage instead of falling back to the
 * product's own base rate or 0 (ENG-1614). Returns null when the rate has no override for this
 * product, so callers can tell "no override" apart from a real 0.
 */
export function getRateDefaultProductCoverage({
  rate,
  productKey
}: {
  rate: Pick<EditableRate, 'defaultProducts'>
  productKey: string | undefined
}): number | null {
  const defaultProduct = rate?.defaultProducts?.find((product) => product.productKey === productKey)

  if (!defaultProduct || isEmptyRateCoverage(defaultProduct.coverage)) {
    return null
  }

  return parseNumber(defaultProduct.coverage, 0)
}

/**
 * The coat count a rate has configured for a specific default product, the sibling of
 * getRateDefaultProductCoverage above.
 *
 * A product's coat count lives on the quote slot, not on the product library entry, so choosing a
 * different product for a slot has nothing of its own to read. This is what the rate says that
 * product should use. Returns null when the rate has no coats for this product, so callers can fall
 * back to whatever the slot already had rather than treat it as 0 (#23899).
 */
export function getRateDefaultProductCoats({
  rate,
  productKey
}: {
  rate: Pick<EditableRate, 'defaultProducts'>
  productKey: string | undefined
}): number | null {
  const defaultProduct = rate?.defaultProducts?.find((product) => product.productKey === productKey)

  if (!defaultProduct || typeof defaultProduct.coats === 'undefined' || defaultProduct.coats === null) {
    return null
  }

  // `parseNumber(null)` returns null rather than its fallback, so coerce. A stored 0 means "no coats
  // configured" — every consumer of `coats` treats 0 as absent, and productVolume would price it at
  // zero volume.
  return parseNumber(defaultProduct.coats, 0) || null
}
